import { useContext, useEffect, useState } from 'react';

import { useParams } from 'react-router-dom';

import SkillApi from '../../api/skillApi';
import ButtonLink from '../../components/atoms/anchor/ButtonLink';
import { SectionRowCurrentSectionData } from '../../components/molecules/skill-list/SectionList';
import Card from '../../components/organisms/Card';
import BaseContent from '../../components/organisms/contents/BaseContent';
import DiscussionSwitchSidebarContent from '../../components/organisms/sidebar/discussion/DiscussionSwitchSidebarContent';
import { SectionProgressSidebarContent } from '../../components/organisms/sidebar/SectionProgressSidebarContent';
import { DoubleTabSidebar } from '../../components/organisms/sidebar/Sidebar';
import HeaderArea from '../../components/page-layout/HeaderArea';
import MainArea from '../../components/page-layout/MainArea';
import PageRightSidebar from '../../components/page-layout/PageRightSidebar';
import SidebarArea from '../../components/page-layout/SidebarArea';
import EnrollmentService from '../../services/enrollmentService';
import { StatService } from '../../services/statService';
import { ContentData } from '../../types/contentTypes';
import SkillData from '../../types/skillData';
import { AuthContext } from '../../utils/AuthProvider';
import { useBreadcrumbs } from '../../utils/hooks/useBreadcrumbs';
import useContentsWithProgress from '../../utils/hooks/useContentsWithProgress';
import useSectionsWithProgress from '../../utils/hooks/useSectionsWithProgress';

import './Section.scss';

export default function Section() {
  const [skill, setSkill] = useState<SkillData>();

  const { sectionSlug, skillSlug } = useParams<{ sectionSlug: string; skillSlug: string }>();
  const { user } = useContext(AuthContext)!;
  // eslint-disable-next-line
  const [sections, , markSectionAsComplete] = useSectionsWithProgress(skill, user);
  const section = sections?.find(section => section.slug === sectionSlug);
  const [contents, markContentAsComplete] = useContentsWithProgress(section, user);
  const breadcrumbs = useBreadcrumbs([
    ['/home', 'Home'],
    [`/skills/${skillSlug!}`, skill?.name],
    ['#', section?.name],
  ]);
  const [currentContentId, setCurrentContentId] = useState<string>('');

  const currentSectionData: SectionRowCurrentSectionData | undefined =
    section && contents
      ? {
          section,
          contents,
        }
      : undefined;

  const nextSection = (() => {
    if (!section || !sections) return;
    const currentSectionIndex = sections.findIndex(currentSection => currentSection.id === section.id);
    const nextSectionIndex = currentSectionIndex !== -1 ? currentSectionIndex + 1 : currentSectionIndex;
    if (nextSectionIndex < sections.length && nextSectionIndex !== -1) {
      return sections[nextSectionIndex];
    } else {
      return null;
    }
  })();

  useEffect(() => {
    async function fetchData() {
      const fetchedSkill = await SkillApi.getSkillBySlug(skillSlug!);
      const isUserEnrolled = await EnrollmentService.isUserEnrolledInSkill(user, fetchedSkill);
      if (!isUserEnrolled) {
        await EnrollmentService.enrollUserInSkill(user!, fetchedSkill);
      }
      setSkill(fetchedSkill);
    }
    fetchData();
  }, [sectionSlug, skillSlug, user]);

  // Scroll to top when going to next section. Navigation in React doesn't cause a re-mount if 'to' link points to the
  // same page component which makes it necessary to manually set the scroll position.
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [sectionSlug]);

  async function handleContentComplete(completedContent: ContentData) {
    if (!contents || !section || !sections || !user) return;
    // Update db
    const { sectionComplete } = await StatService.markContentAsCompleted(completedContent, user);
    // Update hook values to reflect changes in db
    markContentAsComplete(completedContent);
    if (sectionComplete) {
      markSectionAsComplete(section);
    }
  }

  const Contents = contents?.map(content => {
    return (
      <BaseContent
        key={content.id}
        className="line-bottom mb-20"
        content={content}
        onComplete={() => handleContentComplete(content)}
        setCurrentContent={setCurrentContentId}
      />
    );
  });

  return (
    <PageRightSidebar className="section" breadcrumbs={breadcrumbs}>
      <HeaderArea />
      <MainArea>
        <Card className="sm:p-16 p-4">
          <div className="mb-16">{Contents}</div>
          <div className="flex justify-between">
            <ButtonLink className="" visualType="secondary" to={`/skills/${skillSlug}`}>
              Back to overview
            </ButtonLink>
            {nextSection && section && (
              <ButtonLink
                visualType={section.complete ? 'primary' : 'secondary'}
                className=""
                to={`/skills/${skillSlug}/sections/${nextSection.slug}`}
              >
                Next section
              </ButtonLink>
            )}
          </div>
        </Card>
      </MainArea>
      <SidebarArea>
        <DoubleTabSidebar
          tabLabels={['Your progress', 'Discussion']}
          component1={
            skill &&
            sections &&
            currentSectionData && (
              <SectionProgressSidebarContent
                skill={skill}
                currentSectionData={currentSectionData}
                sections={sections}
                currentContentId={currentContentId}
              />
            )
          }
          component2={section && <DiscussionSwitchSidebarContent contextId={section.id} context="SECTION" />}
        />
      </SidebarArea>
    </PageRightSidebar>
  );
}
